import {
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

// Brotia runs in a *second*, named Firebase app so it never collides with the
// default Posy app used for the merchant's own auth/data. The config is fetched
// at runtime from brotia rather than hard-coded.
const BROTIA_APP_NAME = 'brotia';

export const BROTIA_BASE_URL =
  process.env.REACT_APP_BROTIA_BASE_URL ?? 'https://messenger.brotia.com';

let cachedApp: FirebaseApp | null = null;
let cachedConfig: FirebaseOptions | null = null;
let cachedAuth: Auth | null = null;
let cachedDb: Firestore | null = null;
let cachedStorage: FirebaseStorage | null = null;

const fetchBrotiaConfig = async (): Promise<FirebaseOptions> => {
  if (cachedConfig) return cachedConfig;
  const response = await fetch(`${BROTIA_BASE_URL}/api/firebase-config`);
  if (!response.ok) {
    throw new Error(
      `Failed to load brotia Firebase config (${response.status})`,
    );
  }
  cachedConfig = (await response.json()) as FirebaseOptions;
  return cachedConfig;
};

const getBrotiaApp = async (): Promise<FirebaseApp> => {
  if (cachedApp) return cachedApp;
  const existing = getApps().find((a) => a.name === BROTIA_APP_NAME);
  if (existing) {
    cachedApp = existing;
    return cachedApp;
  }
  const config = await fetchBrotiaConfig();
  cachedApp = initializeApp(config, BROTIA_APP_NAME);
  return cachedApp;
};

export const getBrotiaAuth = async (): Promise<Auth> => {
  if (cachedAuth) return cachedAuth;
  cachedAuth = getAuth(await getBrotiaApp());
  return cachedAuth;
};

export const getBrotiaDb = async (): Promise<Firestore> => {
  if (cachedDb) return cachedDb;
  cachedDb = getFirestore(await getBrotiaApp());
  return cachedDb;
};

export const getBrotiaStorage = async (): Promise<FirebaseStorage> => {
  if (cachedStorage) return cachedStorage;
  cachedStorage = getStorage(await getBrotiaApp());
  return cachedStorage;
};
